import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ApiService, BulkNotesMode, BulkNotesStats } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';

@Component({
  selector: 'app-bulk-notes-generation-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './bulk-notes-generation-modal.component.html',
  styleUrl: './bulk-notes-generation-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkNotesGenerationModalComponent implements OnChanges, OnDestroy {
  @Input() courseId!: string;
  @Input() isVisible = false;

  @Output() closed = new EventEmitter<void>();
  @Output() generationStarted = new EventEmitter<void>();

  private api = inject(ApiService);
  private toast = inject(ToastService);
  private chRef = inject(ChangeDetectorRef);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);

  constructor() {
    this.iconRegistry.addSvgIcon('other_icons', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/other_icons.svg'));
    this.iconRegistry.addSvgIcon('other_icons_black', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/other_icons_black.svg'));

    
  }

  selectedMode: BulkNotesMode = 'missing';
  isStarting = false;
  isLoadingStats = false;
  stats: BulkNotesStats | null = null;

  get classCountForMode(): number {
    if (!this.stats) return 0;
    return this.selectedMode === 'missing' ? this.stats.missingClasses : this.stats.totalClasses;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible && this.courseId) {
      this.loadStats();
    }
  }

  ngOnDestroy(): void {}

  private async loadStats(): Promise<void> {
    this.isLoadingStats = true;
    this.stats = null;
    this.chRef.markForCheck();
    try {
      this.stats = await firstValueFrom(this.api.getBulkNotesStats(this.courseId));
    } catch {
      // Stats are optional — modal still works without them
    } finally {
      this.isLoadingStats = false;
      this.chRef.markForCheck();
    }
  }

  async startGeneration(): Promise<void> {
    this.isStarting = true;
    this.chRef.markForCheck();
    try {
      await firstValueFrom(this.api.generateBulkClassNotesAdmin(this.courseId, this.selectedMode));
      this.generationStarted.emit();
      this.close();
    } catch (err: any) {
      const message = err?.error?.message ?? 'Error al iniciar la generación. Inténtalo de nuevo.';
      this.toast.presentErrorToast(message);
    } finally {
      this.isStarting = false;
      this.chRef.markForCheck();
    }
  }

  close(): void {
    this.selectedMode = 'missing';
    this.stats = null;
    this.closed.emit();
  }
}
